/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceSupabase - A library for working with supabase made in Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.
*/

import { Injectable, inject } from "@angular/core";
import type {
	FiltersInput,
	ItemsService,
} from "@app/@classes/abstract/items-service-interface";
import { ItemsViewServiceBase } from "@app/@classes/abstract/items-view-service";
import { ToastService } from "@app/widgets/base/notification/toast.service";
import { type Observable, catchError, from, map, of } from "rxjs";
import type { Views, ViewsKeys } from "../../@classes/supabase";

const VIEW: ViewsKeys = "users_in_tenant";
type T = typeof VIEW;

@Injectable({
	providedIn: "root",
})
export class UsersTenantsService
	extends ItemsViewServiceBase<Views<"users_in_tenant">>
	implements ItemsService<Views<"users_in_tenant">> {
	constructor() {
		super(VIEW, { hasTenant: true });
	}

	protected readonly toastService = inject(ToastService);

	defaultItem(): Views<T> {
		return {
			id: this.randomUUID(),
			user_id: "",
			email: "",
			name: "",
			custom_initials: "",
			avatar_type: "text",
			profile_img: "",
			invitation_token: this.randomUUID(),
			invitation_email: null,
			invitation_status: null,
			invited_by_user_id: null,
			role: "member",
			tenant_id: "",
			created_at: new Date().toISOString(),
			updated_at: new Date().toISOString(),
		};
	}

	public getUsersForTable(
		filtersInput?: FiltersInput<Views<T>>,
	): Observable<{ data: Views<T>[]; count: number }> {
		const tenantId = this.authStateService.currentTenantId();
		if (!tenantId) {
			return of({
				data: [],
				count: 0,
			});
		}

		return from(
			this.supabaseClient
				.from("users_in_tenant")
				.select("*", { count: "exact" })
				.eq("tenant_id", tenantId)
				.neq("role", "member")
				.order("created_at", { ascending: false })
				.range(filtersInput?.from ?? 0, filtersInput?.to ?? 1000),
		).pipe(
			catchError((error) => this.errorHandler(error)),
			map((response) => {
				if (response.error) throw response.error;
				return {
					data: response.data ?? [],
					count: response.count ?? 0,
				};
			}),
		);
	}

	public inviteUser(
		email: string,
		tenantId: string,
		baseInvitationPath: string,
		role: "member" | "admin",
		canSeeUnassignedTickets: boolean,
		canSeeOthersTickets: boolean,
		canUploadFiles: boolean,
		canSendMessages: boolean,
	) {
		return from(
			this.trpcService.client.auth.sendInvitationByEmail.mutate({
				email: email,
				tenantId: tenantId,
				baseInvitationPath: baseInvitationPath,
				role: role,
				canSeeUnassignedTickets: canSeeUnassignedTickets,
				canSeeOthersTickets: canSeeOthersTickets,
				canUploadFiles: canUploadFiles,
				canSendMessages: canSendMessages,
			}),
		);
	}
}
